/**
 * Shows what changed in a task's request and rubric compared with an earlier version of it.
 * By default that is the snapshot taken when the expert submitted the draft, so the engineer's
 * draft-review edits are marked: on the expert's task page (execution stage and after a
 * send-back), in the draft editor, and on the engineer's page. A peer review passes the
 * previous review round instead, so the reviewer sees what the author changed since then.
 */
import { useState } from 'react'
import { RubricView } from './RubricView'
import { wordDiff, hasChanges } from '@/lib/diff'
import type { DraftSnapshot, RubricCriterion, Task } from '@/lib/types'

export interface RubricChange {
  kind: 'added' | 'removed' | 'changed'
  before?: RubricCriterion
  after?: RubricCriterion
}

export interface ReviewDiff {
  promptChanged: boolean
  rubric: RubricChange[]
  layoutChanged: boolean
  any: boolean
}

/** The earlier version a task is compared with. */
export type Baseline = Pick<DraftSnapshot, 'prompt' | 'rubric' | 'rubric_layout'>
type Compared = Pick<Task, 'prompt' | 'rubric' | 'rubric_layout'>

const critKey = (c: RubricCriterion) => JSON.stringify([c.criterion, c.role, c.evidence ?? '', c.rationale ?? '', c.decision_anchor ?? '', c.source ?? '', c.visibility ?? '', c.judgment ?? ''])

/** Compute the changes from `before` to `task`, or null when there is nothing to compare with. */
export function reviewDiff(task: Compared & Partial<Pick<Task, 'draft_snapshot'>>, before: Baseline | null | undefined = task.draft_snapshot): ReviewDiff | null {
  if (!before) return null
  const promptChanged = hasChanges(wordDiff(before.prompt ?? '', task.prompt ?? ''))
  const previous = new Map((before.rubric ?? []).map((c) => [c.criterion_id, c]))
  const after = new Map((task.rubric ?? []).map((c) => [c.criterion_id, c]))
  const rubric: RubricChange[] = []
  for (const [id, b] of previous) {
    const a = after.get(id)
    if (!a) rubric.push({ kind: 'removed', before: b })
    else if (critKey(a) !== critKey(b)) rubric.push({ kind: 'changed', before: b, after: a })
  }
  for (const [id, a] of after) if (!previous.has(id)) rubric.push({ kind: 'added', after: a })
  const layoutChanged = JSON.stringify(task.rubric_layout ?? []) !== JSON.stringify(before.rubric_layout ?? [])
  return { promptChanged, rubric, layoutChanged, any: promptChanged || rubric.length > 0 || layoutChanged }
}

/** Text with the words added and removed since `before` marked inline. */
export function MarkedText({ before, after }: { before: string; after: string }) {
  return <>{wordDiff(before, after).map((o, i) => o.kind === 'same' ? <span key={i}>{o.text}</span>
    : o.kind === 'add' ? <ins key={i}>{o.text}</ins> : <del key={i}>{o.text}</del>)}</>
}

/** The request text with the review's edits marked inline. Falls back to plain text when unchanged. */
export function PromptWithChanges({ task, label = 'Changes since draft submission' }: { task: Task; label?: string }) {
  const snap = task.draft_snapshot
  const [showOriginal, setShowOriginal] = useState(false)
  if (!snap || !hasChanges(wordDiff(snap.prompt, task.prompt))) return <pre>{task.prompt}</pre>
  return (
    <div className="changed-block">
      <div className="changed-head">
        <span className="chip warn">{label}</span>
        <button type="button" className="link small" onClick={() => setShowOriginal(!showOriginal)}>{showOriginal ? 'Show marked changes' : 'Show what you submitted'}</button>
      </div>
      {showOriginal
        ? <pre>{snap.prompt}</pre>
        : <pre className="diff"><MarkedText before={snap.prompt} after={task.prompt} /></pre>}
    </div>
  )
}

/** Summary banner listing every change the review made. Renders nothing when there is none. */
export function ReviewChangesSummary({ task }: { task: Task; who?: string }) {
  const d = reviewDiff(task)
  if (!d?.any) return null
  return (
    <div className="note warn changes-note">
      <div className="who">Changes since draft submission</div>
      {changeParts(d).join(', ')} compared with the draft you submitted. The changes are marked below.
    </div>
  )
}

/** What changed, in short phrases: "agent prompt changed", "2 rubric criteria added". */
export function changeParts(d: ReviewDiff, briefChanged = false): string[] {
  const counts = { added: 0, removed: 0, changed: 0 }
  for (const c of d.rubric) counts[c.kind]++
  const parts: string[] = []
  if (d.promptChanged) parts.push('agent prompt changed')
  if (briefChanged) parts.push('brief changed')
  if (d.layoutChanged) parts.push('rubric organisation changed')
  if (counts.added) parts.push(`${counts.added} rubric criteri${counts.added === 1 ? 'on' : 'a'} added`)
  if (counts.changed) parts.push(`${counts.changed} rubric criteri${counts.changed === 1 ? 'on' : 'a'} changed`)
  if (counts.removed) parts.push(`${counts.removed} rubric criteri${counts.removed === 1 ? 'on' : 'a'} removed`)
  return parts
}

const roleChip = (role: RubricCriterion['role']) => `chip ${role === 'required' ? 'neutral' : role === 'bonus' ? 'ok' : 'err'}`

/** A criterion with its words added and removed since `was` marked inline, like the brief; new criteria are all added. */
function InlineCriterion({ c, change }: { c: RubricCriterion; change?: RubricChange }) {
  const was = change?.kind === 'changed' ? change.before : undefined
  const added = change?.kind === 'added'
  const text = (before: string | undefined, after: string) => added ? <ins>{after}</ins> : <MarkedText before={before ?? after} after={after} />
  return <>
    <span className={`id${added ? ' is-added' : ''}`}>{c.criterion_id}</span>
    <div className="diff">
      <div className="crit">{text(was?.criterion, c.criterion)}</div>
      <div className="tags">
        {was && was.role !== c.role
          ? <><del className={roleChip(was.role)}>{was.role}</del><ins className={roleChip(c.role)}>{c.role}</ins></>
          : <span className={roleChip(c.role)}>{added ? <ins>{c.role}</ins> : c.role}</span>}
      </div>
      {(c.evidence || was?.evidence) && <div className="detail"><b>Check:</b> {text(was ? was.evidence ?? '' : undefined, c.evidence ?? '')}</div>}
    </div>
  </>
}

/**
 * Rubric with changes marked per criterion (added, changed with the previous text, removed struck
 * through). `before` defaults to the draft snapshot; `since` ends each marker ("added in review").
 * `inline` marks the words that changed in place, as the brief does, instead of highlighting rows;
 * a new criterion is green throughout, id included, and a removed one red and struck through.
 */
export function RubricWithChanges({ task, emptyText = 'No rubric yet.', before, since = 'in review', inline = false }: {
  task: Compared & Partial<Pick<Task, 'draft_snapshot'>>; emptyText?: string; before?: Baseline | null; since?: string; inline?: boolean
}) {
  const d = before === undefined ? reviewDiff(task) : reviewDiff(task, before)
  const removed = d?.rubric.filter((c) => c.kind === 'removed') ?? []
  const byId = new Map(d?.rubric.filter((c) => c.kind !== 'removed').map((c) => [c.after!.criterion_id, c]))
  if (!task.rubric.length && removed.length === 0) return <p className="empty">{emptyText}</p>
  if (inline) return (
    <>
      <RubricView rubric={task.rubric} layout={task.rubric_layout} emptyText={emptyText}
        renderCriterion={c => <InlineCriterion c={c} change={byId.get(c.criterion_id)} />} />
      {!!removed.length && <ol className="rubric">
      {removed.map((r) => (
        <li key={`removed-${r.before!.criterion_id}`}>
          <span className="id is-removed">{r.before!.criterion_id}</span>
          <div className="diff">
            <div className="crit"><del>{r.before!.criterion}</del></div>
            <div className="tags"><del className={roleChip(r.before!.role)}>{r.before!.role}</del></div>
            {r.before!.evidence && <div className="detail"><b>Check:</b> <del>{r.before!.evidence}</del></div>}
          </div>
        </li>
      ))}
      </ol>}
    </>
  )
  return (
    <>
      <RubricView rubric={task.rubric} layout={task.rubric_layout} emptyText={emptyText} criterionClassName={c => {
        const change = byId.get(c.criterion_id)
        return change ? `is-${change.kind}` : ''
      }} renderCriterion={c => {
        const change = byId.get(c.criterion_id)
        const was = change?.kind === 'changed' ? change.before : undefined
        return <>
          <span className="id">{c.criterion_id}</span>
          <div>
            <div className="crit">{c.criterion}{change && <span className={`chip ${change.kind === 'added' ? 'ok' : 'warn'}`} style={{ marginLeft: '.5rem' }}>{change.kind === 'added' ? `added ${since}` : `changed ${since}`}</span>}</div>
            {was && was.criterion !== c.criterion && <div className="detail was"><b>Was:</b> {was.criterion}</div>}
            <div className="tags"><span className={`chip ${c.role === 'required' ? 'neutral' : c.role === 'bonus' ? 'ok' : 'err'}`}>{c.role}</span>
              {was && was.role !== c.role && <span className="detail was small">was {was.role}</span>}</div>
            {c.evidence && <div className="detail"><b>Check:</b> {c.evidence}</div>}
            {was && (was.evidence ?? '') !== (c.evidence ?? '') && <div className="detail was"><b>Check was:</b> {was.evidence || '(none)'}</div>}
          </div>
        </>
      }} />
      {!!removed.length && <ol className="rubric">
      {removed.map((r) => (
        <li key={`removed-${r.before!.criterion_id}`} className="is-removed">
          <span className="id">{r.before!.criterion_id}</span>
          <div><div className="crit"><del>{r.before!.criterion}</del> <span className="chip err">removed {since}</span></div></div>
        </li>
      ))}
      </ol>}
    </>
  )
}
